import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { BoostaBolt } from '@/components/BoostaBolt';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { toast.error('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      toast.success('Password updated — please sign in.');
      nav('/login', { replace: true });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-boosta-ink flex items-center justify-center px-4 relative overflow-hidden aurora">
      <div className="absolute inset-0 grid-bg opacity-30"/>
      <div className="absolute inset-0 grain"/>
      <div className="absolute top-6 left-6 z-10">
        <Link to="/" className="flex items-center gap-2"><BoostaBolt size={28} animate/><span className="font-display font-bold text-3d">Boosta</span></Link>
      </div>
      <div className="relative w-full max-w-md z-10">
        <div className="glass rounded-3xl p-8 md:p-10" data-testid="reset-root">
          {!token ? (
            <div className="text-center" data-testid="reset-no-token">
              <h1 className="font-display font-black text-2xl text-3d">Invalid reset link</h1>
              <p className="text-white/55 text-sm mt-2">This link is missing its token. Request a new one.</p>
              <Link to="/forgot-password" className="mt-6 inline-block btn-primary rounded-full px-5 py-2.5 text-sm">Request new link</Link>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">New password</div>
              <h1 className="font-display font-black text-3xl tracking-tight text-3d">Set a new password</h1>
              <p className="text-white/50 text-sm mt-1.5">Choose a strong password you don't use elsewhere.</p>

              <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block mt-8">New password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2"/>
                <input type={showPw ? 'text' : 'password'} required value={password} onChange={e=>setPassword(e.target.value)} data-testid="reset-password"
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-11 py-3 text-sm focus:border-boosta-cyan/50 outline-none transition" placeholder="6+ characters"/>
                <button type="button" onClick={()=>setShowPw(v=>!v)} data-testid="reset-toggle-password" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-boosta-cyan transition">
                  {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>

              <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block mt-4">Confirm password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2"/>
                <input type={showPw ? 'text' : 'password'} required value={confirm} onChange={e=>setConfirm(e.target.value)} data-testid="reset-confirm"
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm focus:border-boosta-cyan/50 outline-none transition" placeholder="Re-enter password"/>
              </div>

              <button disabled={busy} type="submit" data-testid="reset-submit"
                className="mt-6 w-full btn-primary rounded-xl py-3.5 font-bold disabled:opacity-40">
                {busy ? 'Updating…' : 'Update password'}
              </button>
              <div className="mt-6 text-center">
                <Link to="/login" className="inline-flex items-center gap-2 text-sm text-white/45 hover:text-boosta-cyan"><ArrowLeft className="w-4 h-4"/> Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
